/**
 * Site-wide marketing footer (same markup as the home page). Pass `site` + `headerSection` from CMS/defaults.
 */
const FOOTER_BG =
  'https://agrezen.zozothemes.com/wp-content/uploads/2025/10/footer-3.webp';

const SCALLOPED_BG =
  'M50 3L63.78 16.74L83.23 16.77L83.26 36.22L97 50L83.26 63.78L83.23 83.23L63.78 83.26L50 97L36.22 83.26L16.77 83.23L16.74 63.78L3 50L16.74 36.22L16.77 16.77L36.22 16.74 Z';

function ArrowUpRight({ className }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M4 12L12 4M12 4H6M12 4V10"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconPhoneHandset() {
  return (
    <g
      transform="translate(50,52)"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M-10 -14c0-3 2.5-6 6-6h2c3.5 0 6 2.5 6 6v28c0 3.5-2.5 6-6 6h-2c-3.5 0-6-2.5-6-6V-14z" />
      <path d="M-4 18h4" />
    </g>
  );
}

function IconEnvelope() {
  return (
    <g
      transform="translate(50,52)"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="-16" y="-11" width="32" height="22" rx="2.5" />
      <path d="M-16 -11l16 14 16-14" />
    </g>
  );
}

function FooterContactScallop({ children }) {
  return (
    <svg className="h-11 w-11 shrink-0 sm:h-12 sm:w-12" viewBox="0 0 100 100" aria-hidden>
      <path className="fill-brand-lime" d={SCALLOPED_BG} />
      <g className="text-white">{children}</g>
    </svg>
  );
}

function FooterSectionTitle({ children }) {
  return (
    <p className="mb-6 flex items-center gap-2.5 text-base font-bold leading-tight tracking-tight text-white md:mb-7 md:text-[1.0625rem]">
      <svg className="h-3.5 w-3.5 shrink-0 text-brand-lime" viewBox="0 0 100 100" aria-hidden>
        <path fill="currentColor" d={SCALLOPED_BG} />
      </svg>
      {children}
    </p>
  );
}

function SocialCircle({ href, label, brandClass, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className={`flex h-10 w-10 items-center justify-center rounded-full border-0 text-white shadow-md ring-1 ring-black/15 transition-[transform,opacity,filter] hover:brightness-110 hover:opacity-95 active:scale-[0.96] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 ${brandClass}`}
    >
      {children}
    </a>
  );
}

export default function SiteFooter({ site, headerSection }) {
  const footer = site?.footer && typeof site.footer === 'object' ? site.footer : {};
  const serviceLinks = Array.isArray(footer.serviceLinks) ? footer.serviceLinks : [];
  const social = footer.social && typeof footer.social === 'object' ? footer.social : {};

  return (
    <footer
      id="site-footer"
      className="relative isolate overflow-hidden text-white"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(13, 31, 22, 0.88) 0%, rgba(8, 22, 16, 0.92) 50%, rgba(10, 26, 18, 0.94) 100%), url(${FOOTER_BG})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="relative z-[1] mx-auto max-w-shell px-6 pb-10 pt-16 md:pb-12 md:pt-20 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-3 lg:gap-0 lg:divide-x lg:divide-white/15">
          <div className="lg:pr-10 xl:pr-14">
            <a
              href={headerSection?.brandHref || '/'}
              className="mb-8 inline-flex items-center gap-2.5 text-inherit no-underline sm:gap-3"
            >
              <img
                src={headerSection?.logoUrl}
                alt={headerSection?.logoAlt || ''}
                className="h-11 w-11 shrink-0 rounded-full object-cover object-center ring-1 ring-white/20 sm:h-12 sm:w-12"
                width={120}
                height={120}
                decoding="async"
              />
              <span className="flex flex-col leading-[1.1] min-[400px]:flex-row min-[400px]:items-baseline min-[400px]:gap-1.5">
                <span className="text-[1.35rem] font-bold tracking-tight text-white">
                  {headerSection?.brandPrimary}
                </span>
                <span className="text-[1.35rem] font-semibold tracking-tight text-brand-lime">
                  {headerSection?.brandSecondary}
                </span>
              </span>
            </a>

            <ul className="m-0 flex list-none flex-col gap-5 p-0">
              <li>
                <a
                  href={footer.telHref || '#'}
                  className="group flex items-center gap-3.5 no-underline transition-opacity hover:opacity-95"
                >
                  <FooterContactScallop>
                    <IconPhoneHandset />
                  </FooterContactScallop>
                  <span className="text-[0.95rem] font-medium leading-snug text-white/95 md:text-base">
                    {footer.phone || 'Call us'}
                  </span>
                </a>
              </li>
              <li>
                <a
                  href={footer.mailtoHref || '#'}
                  className="group flex items-center gap-3.5 no-underline transition-opacity hover:opacity-95"
                >
                  <FooterContactScallop>
                    <IconEnvelope />
                  </FooterContactScallop>
                  <span className="text-[0.95rem] font-medium leading-snug text-white/95 md:text-base">
                    {footer.email || 'Email us'}
                  </span>
                </a>
              </li>
            </ul>
          </div>

          <div className="lg:px-10 xl:px-14">
            <FooterSectionTitle>Our Services</FooterSectionTitle>
            <nav aria-label="Footer services">
              <ul className="m-0 flex list-none flex-col gap-3.5 p-0">
                {serviceLinks.map(({ label, href }) => (
                  <li key={label}>
                    <a
                      href={href}
                      className="group inline-flex items-start gap-2.5 text-[0.95rem] font-normal leading-snug text-white/90 no-underline transition-colors hover:text-white"
                    >
                      <ArrowUpRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-lime opacity-95 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          <div className="lg:pl-10 xl:pl-14">
            <p className="mb-8 max-w-md text-sm leading-relaxed text-white/78 md:text-[0.95rem]">
              {footer.blurb || ''}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              {social.facebook ? (
                <SocialCircle href={social.facebook} label="Facebook" brandClass="bg-[#1877F2]">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M13.5 22v-8.3h2.8l.4-3.3h-3.2V8.6c0-.9.3-1.6 1.6-1.6h1.7V4.1c-.3 0-1.4-.1-2.6-.1-2.6 0-4.4 1.6-4.4 4.5v2.5H7.5v3.3H9.5V22h4z" />
                  </svg>
                </SocialCircle>
              ) : null}
              {social.youtube ? (
                <SocialCircle href={social.youtube} label="YouTube" brandClass="bg-[#FF0000]">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M21.6 7.2c-.2-1-1-1.8-2-2C17.8 4.5 12 4.5 12 4.5s-5.8 0-7.6.7c-1 .2-1.8 1-2 2C2 9 2 12 2 12s0 3 .4 4.8c.2 1 1 1.8 2 2 1.8.7 7.6.7 7.6.7s5.8 0 7.6-.7c1-.2 1.8-1 2-2 .4-1.8.4-4.8.4-4.8s0-3-.4-4.8zM10 15.3V8.7l5.2 3.3-5.2 3.3z" />
                  </svg>
                </SocialCircle>
              ) : null}
            </div>
          </div>
        </div>

        <div className="mt-14 border-t border-white/20 pt-8 md:mt-16 md:pt-10">
          <p className="m-0 max-w-3xl text-center text-sm leading-relaxed text-white/65 md:text-left">
            © Copyright {new Date().getFullYear()}. All rights reserved.{' '}
            <span className="font-semibold text-brand-lime">{footer.copyrightName || ''}</span>. Developed by{' '}
            <a
              href={footer.developerUrl || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-brand-lime no-underline hover:underline"
            >
              {footer.developerName || ''}
            </a>
            .
          </p>
        </div>
      </div>
    </footer>
  );
}
